import { useState, useRef, useEffect, useCallback } from 'react';
import { TransformWrapper, TransformComponent, ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import { FileText, Download, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { SmartDocumentViewerProps, getContentType, ZOOM_MIN, ZOOM_MAX } from './types';
import { SmartViewerControls } from './SmartViewerControls';
import { PDFContent } from './PDFContent';
import { ImageContent } from './ImageContent';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

export function SmartDocumentViewer({
  url,
  title,
  mimeType,
  fileData,
  className,
  showControls = true,
  onLoad,
  onError,
}: SmartDocumentViewerProps) {
  const [rotation, setRotation] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [containerWidth, setContainerWidth] = useState(800);
  const [canZoomIn, setCanZoomIn] = useState(true);
  const [canZoomOut, setCanZoomOut] = useState(true);
  const [currentScale, setCurrentScale] = useState(1);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const transformRef = useRef<ReactZoomPanPinchRef>(null);

  const contentType = getContentType(mimeType);

  // Measure container width for fit-width behavior
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };

    updateWidth();
    
    const resizeObserver = new ResizeObserver(updateWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  const handleLoadSuccess = useCallback((numPages?: number) => {
    setIsLoading(false);
    setError(null);
    onLoad?.();
  }, [onLoad]);

  const handleLoadError = useCallback((errorMessage: string) => {
    setIsLoading(false);
    setError(errorMessage);
    onError?.(errorMessage);
  }, [onError]);

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleZoomChange = (ref: ReactZoomPanPinchRef) => {
    const scale = ref.state.scale;
    setCurrentScale(scale);
    setCanZoomIn(scale < ZOOM_MAX);
    setCanZoomOut(scale > ZOOM_MIN);
  };

  const handleOpenOriginal = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = title || 'document';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('Download failed:', err);
      // Fallback: open in new tab
      window.open(url, '_blank');
    }
  };

  // Unsupported format
  if (contentType === 'unsupported') {
    return (
      <div className={cn("flex flex-col items-center justify-center h-full gap-4 p-8", className)}>
        <FileText className="w-16 h-16 text-muted-foreground/50" />
        <p className="text-muted-foreground text-center">
          Ce format de fichier ne peut pas être prévisualisé
        </p>
        <div className="flex gap-3">
          <Button variant="outline" onClick={handleDownload} className="gap-2">
            <Download className="w-4 h-4" />
            Télécharger
          </Button>
          <Button variant="default" onClick={handleOpenOriginal} className="gap-2">
            <ExternalLink className="w-4 h-4" />
            Ouvrir le fichier
          </Button>
        </div>
      </div>
    );
  }

  // Error state with fallback action
  if (error) {
    return (
      <div className={cn("flex flex-col items-center justify-center h-full gap-4 p-8", className)}>
        <FileText className="w-16 h-16 text-destructive/50" />
        <p className="text-destructive font-medium text-center">
          Impossible de prévisualiser ce document
        </p>
        <p className="text-muted-foreground text-sm text-center max-w-md">
          Le fichier est peut-être corrompu ou dans un format non supporté.
        </p>
        <Button 
          variant="default" 
          size="lg" 
          onClick={handleOpenOriginal} 
          className="gap-2 mt-2"
        >
          <ExternalLink className="w-5 h-5" />
          Ouvrir le fichier original
        </Button>
        <Button 
          variant="outline" 
          onClick={handleDownload} 
          className="gap-2"
        >
          <Download className="w-4 h-4" />
          Télécharger
        </Button>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className={cn(
        "relative w-full h-full overflow-y-auto overflow-x-hidden bg-secondary/30 rounded-lg",
        className
      )}
    >
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/50 backdrop-blur-sm z-10">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      {/* Contrôles flottants - EN DEHORS du TransformWrapper pour rester fixes */}
      {showControls && !error && !isLoading && (
        <SmartViewerControls
          onZoomIn={() => transformRef.current?.zoomIn(0.3)}
          onZoomOut={() => transformRef.current?.zoomOut(0.3)}
          onReset={() => transformRef.current?.resetTransform()}
          onRotate={handleRotate}
          canZoomIn={canZoomIn}
          canZoomOut={canZoomOut}
        />
      )}

      <TransformWrapper
        ref={transformRef}
        initialScale={1}
        minScale={ZOOM_MIN}
        maxScale={ZOOM_MAX}
        centerOnInit={false}
        limitToBounds={false}
        panning={{ 
          velocityDisabled: true,
          disabled: currentScale <= 1, // Désactiver le pan quand pas zoomé (permet scroll natif)
        }}
        pinch={{ step: 10 }}
        doubleClick={{ mode: 'zoomIn', step: 0.5 }}
        onTransformed={handleZoomChange}
        wheel={{ 
          activationKeys: ['Control', 'Meta'],
          step: 0.1,
          smoothStep: 0.005,
        }}
      >
        {() => (
          <>
            <TransformComponent
              wrapperClass="!w-full"
              contentClass="w-full"
              wrapperStyle={{ 
                width: '100%', 
                overflow: 'visible',
              }}
              contentStyle={{ 
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                padding: '1rem',
              }}
            >
              <div className="w-full max-w-4xl">
                {contentType === 'pdf' ? (
                  <PDFContent
                    url={url}
                    containerWidth={containerWidth}
                    rotation={rotation}
                    onLoadSuccess={handleLoadSuccess}
                    onLoadError={handleLoadError}
                  />
                ) : (
                  <ImageContent
                    url={url}
                    title={title || 'Image'}
                    rotation={rotation}
                    onLoad={() => handleLoadSuccess()}
                    onError={handleLoadError}
                  />
                )}
              </div>
            </TransformComponent>
          </>
        )}
      </TransformWrapper>
    </div>
  );
}
